import React, { useState } from 'react';
import { BottomSheet } from './UI/BottomSheet';
import { DishMenuItem, DishPortion } from '../types/menu.types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPrice } from '../config/currency';
import { getImageUrl } from '../config/imageUrl';

/** Данные порции для передачи в callback */
interface PortionUpdateData {
  portion: DishPortion;
  quantity: number;
  itemDiscountPercent?: number;
}

interface DishSelectionModalProps {
  dish: DishMenuItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Callback для обновления порций блюда (заменяет все порции блюда новыми значениями) */
  onUpdateDishPortions: (dish: DishMenuItem, portions: PortionUpdateData[]) => void;
  promoInfo?: {
    is_valid: boolean;
    code?: string;
    discount_percent?: number;
  };
  selectedPortions?: {
    portionId: number;
    quantity: number;
    itemDiscountPercent?: number;
  }[];
}

/** Количество для каждой порции */
interface PortionQuantity {
  [portionId: number]: number;
}

/** Ручная скидка для каждой порции */
interface PortionDiscount {
  [portionId: number]: number;
}

export const DishSelectionModal: React.FC<DishSelectionModalProps> = ({
  dish,
  isOpen,
  onClose,
  onUpdateDishPortions,
  promoInfo,
  selectedPortions = [],
}) => {
  const { t } = useLanguage();
  const [portionQuantities, setPortionQuantities] = useState<PortionQuantity>({});
  const [portionDiscounts, setPortionDiscounts] = useState<PortionDiscount>({});

  // Загружаем уже выбранные граммовки и скидки при открытии
  React.useEffect(() => {
    if (dish && isOpen) {
      const initialQuantities: PortionQuantity = {};
      const initialDiscounts: PortionDiscount = {};
      selectedPortions.forEach(({ portionId, quantity, itemDiscountPercent }) => {
        initialQuantities[portionId] = quantity;
        if (itemDiscountPercent !== undefined) {
          initialDiscounts[portionId] = itemDiscountPercent;
        }
      });
      setPortionQuantities(initialQuantities);
      setPortionDiscounts(initialDiscounts);
    } else if (!isOpen) {
      // Сбрасываем состояние при закрытии
      setPortionQuantities({});
      setPortionDiscounts({});
    }
  }, [dish, isOpen, selectedPortions]);

  const handleQuantityChange = (portionId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      // Удаляем граммовку если количество 0
      const updated = { ...portionQuantities };
      delete updated[portionId];
      setPortionQuantities(updated);
    } else {
      setPortionQuantities({
        ...portionQuantities,
        [portionId]: newQuantity,
      });
    }
  };

  const handleIncreaseQuantity = (portionId: number) => {
    const currentQuantity = portionQuantities[portionId] || 0;
    handleQuantityChange(portionId, currentQuantity + 1);
  };

  const handleDecreaseQuantity = (portionId: number) => {
    const currentQuantity = portionQuantities[portionId] || 0;
    if (currentQuantity > 0) {
      handleQuantityChange(portionId, currentQuantity - 1);
    }
  };

  /**
   * Обработка изменения ручной скидки для порции
   */
  const handleDiscountChange = (portionId: number, discount: number) => {
    // Ограничиваем скидку от 0 до 100
    const clampedDiscount = Math.max(0, Math.min(100, discount));
    setPortionDiscounts((prev) => ({
      ...prev,
      [portionId]: clampedDiscount,
    }));
  };

  const handleAddToOrder = () => {
    if (!dish) return;

    // Собираем все выбранные порции с их количествами и скидками
    const portionsToUpdate: PortionUpdateData[] = [];

    Object.entries(portionQuantities).forEach(([portionId, quantity]) => {
      const portion = dish.portions.find(p => p.id === parseInt(portionId));
      if (portion && quantity > 0) {
        const itemDiscountPercent = portionDiscounts[parseInt(portionId)];
        portionsToUpdate.push({
          portion,
          quantity,
          // Добавляем скидку только если она задана и порция поддерживает скидки
          itemDiscountPercent: portion.allow_item_discount && itemDiscountPercent > 0
            ? itemDiscountPercent
            : undefined,
        });
      }
    });

    // Передаём все порции одним вызовом - это заменит существующие
    onUpdateDishPortions(dish, portionsToUpdate);

    onClose();
  };

  if (!dish) return null;

  /**
   * Расчёт цены порции с учётом всех скидок
   * Порядок применения: сначала ручная скидка, потом промокод
   */
  const calculatePortionPrice = (portion: DishPortion): number => {
    let price = portion.price;

    // Применяем ручную скидку, если она задана
    const itemDiscount = portionDiscounts[portion.id];
    if (portion.allow_item_discount && itemDiscount > 0) {
      price = price * (1 - itemDiscount / 100);
    }

    // Применяем скидку промокода, если он активен и доступен для этой граммовки
    if (promoInfo?.is_valid && promoInfo.discount_percent && portion.allow_promo) {
      price = price * (1 - promoInfo.discount_percent / 100);
    }

    return price;
  };

  const getTotalPrice = () => {
    let total = 0;
    Object.entries(portionQuantities).forEach(([portionId, quantity]) => {
      const portion = dish.portions.find(p => p.id === parseInt(portionId));
      if (portion) {
        const priceWithDiscount = calculatePortionPrice(portion);
        total += priceWithDiscount * quantity;
      }
    });
    return total;
  };

  const hasSelectedPortions = Object.keys(portionQuantities).length > 0;
  // Проверяем, были ли изначально выбраны порции
  const hadInitialPortions = selectedPortions.length > 0;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="space-y-6">
        {/* Изображение и название блюда */}
        <div className="flex flex-col items-center">
          {getImageUrl(dish.image) && (
            <img
              src={getImageUrl(dish.image)!}
              alt={dish.name}
              className="w-full max-w-xs h-48 object-cover rounded-xl mb-4 shadow-md"
            />
          )}
          <h3 className="text-2xl font-bold text-text-primary text-center">
            {dish.name}
          </h3>
        </div>

        {/* Выбор граммовок с счётчиками */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-3">
            {t('addOrder.selectPortion')}:
          </label>
          <div className="space-y-3">
            {dish.portions.map((portion) => {
              const originalPrice = portion.price;
              const discountedPrice = calculatePortionPrice(portion);
              const currentQuantity = portionQuantities[portion.id] || 0;
              const currentDiscount = portionDiscounts[portion.id] || 0;
              const isSelected = currentQuantity > 0;
              // Проверяем есть ли какая-либо скидка (ручная или промокод)
              const hasAnyDiscount =
                (portion.allow_item_discount && currentDiscount > 0) ||
                (promoInfo?.is_valid && portion.allow_promo && promoInfo.discount_percent);

              return (
                <div
                  key={portion.id}
                  className={`
                    p-4 rounded-lg border-2 transition-all
                    ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-background-300 bg-background-100'
                    }
                  `}
                >
                  <div className="flex items-center justify-between gap-4">
                    {/* Информация о граммовке */}
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-lg font-bold text-text-primary">
                          {portion.weight}{t('addOrder.grams')}
                        </span>

                        {/* Бейджик для доступной скидки */}
                        {portion.allow_item_discount && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm6 0a1 1 0 100-2 1 1 0 000 2z" />
                            </svg>
                            {t('addOrder.discountAvailable')}
                          </span>
                        )}

                        {/* Цена с учётом скидки */}
                        {hasAnyDiscount ? (
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm text-text-secondary line-through">
                              {formatPrice(originalPrice)}
                            </span>
                            <span className="text-primary-500 font-semibold">
                              {formatPrice(discountedPrice)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-primary-500 font-semibold">
                            {formatPrice(originalPrice)}
                          </span>
                        )}
                      </div>

                      {/* Индикаторы */}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {promoInfo?.is_valid && portion.allow_promo && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            {t('addOrder.promoAppliedToPortion')}
                          </span>
                        )}
                      </div>

                      {/* Поле ввода ручной скидки (только если allow_item_discount) */}
                      {portion.allow_item_discount && isSelected && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <label className="text-sm text-text-secondary">
                            {t('addOrder.manualDiscount')}:
                          </label>
                          <div className="flex items-center gap-1">
                            {/* Кнопка -5% */}
                            <button
                              onClick={() => handleDiscountChange(portion.id, currentDiscount - 5)}
                              className="w-9 h-8 flex items-center justify-center bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors text-sm font-bold"
                              disabled={currentDiscount <= 0}
                            >
                              -5
                            </button>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={currentDiscount || ''}
                              onChange={(e) => handleDiscountChange(portion.id, parseInt(e.target.value) || 0)}
                              placeholder="0"
                              className="w-14 px-2 py-1 text-center border border-purple-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            {/* Кнопка +5% */}
                            <button
                              onClick={() => handleDiscountChange(portion.id, currentDiscount + 5)}
                              className="w-9 h-8 flex items-center justify-center bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors text-sm font-bold"
                              disabled={currentDiscount >= 100}
                            >
                              +5
                            </button>
                            <span className="text-sm text-text-secondary">%</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Счётчик для каждой граммовки */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDecreaseQuantity(portion.id)}
                        className="w-9 h-9 flex items-center justify-center bg-background-200 hover:bg-background-300 rounded-lg transition-colors text-lg font-bold disabled:opacity-50"
                        disabled={currentQuantity === 0}
                      >
                        −
                      </button>
                      <div className="w-12 text-center text-xl font-bold text-text-primary">
                        {currentQuantity}
                      </div>
                      <button
                        onClick={() => handleIncreaseQuantity(portion.id)}
                        className="w-9 h-9 flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-lg font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Итоговая цена и кнопка добавления */}
        <div className="space-y-4 pt-4 border-t-2 border-background-300">
          {hasSelectedPortions && (
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-text-secondary">
                {t('addOrder.totalPrice')}:
              </span>
              <span className="text-2xl font-bold text-primary-500">
                {formatPrice(getTotalPrice())}
              </span>
            </div>
          )}

          <button
            onClick={handleAddToOrder}
            disabled={!hasSelectedPortions && !hadInitialPortions}
            className="btn btn-primary w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasSelectedPortions ? t('addOrder.addToOrder') : (hadInitialPortions ? t('addOrder.removeFromOrder') : t('addOrder.addToOrder'))}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};
